import { RootState } from "core-integration/src/store";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { IPickLight } from "core-integration/src/store/entries/types";
import { getFixturesStatusForTeamInEvent } from "core-integration/src/store/fixtures/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import {
  getMyPicksProposed,
  getMyPicksSaved,
} from "core-integration/src/store/my-team/reducers";
import { SubStatus } from "core-integration/src/store/my-team/types";
import { size } from "polished";
import * as React from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import Sub from "../../img/icons/sub.svg?react";
import { getElementValueBarVariant } from "../../utils/getElementValueBarVariant";
import ElementDialogButton from "../ElementDialogButton";
import ElementShirt from "../ElementShirt";
import ElementValueBar from "../ElementValueBar";
import {
  Controls,
  InfoControl,
  PitchElementWrap,
  PrimaryControl,
  StyledPitchElement,
} from "../Pitch";
import PitchElementData from "../PitchElementData";
import { UnstyledButton } from "../Utils";
import TeamElementIcon from "./TeamElementIcon";

const StyledSub = styled(Sub)`
  ${size(14)}

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    ${size(16)}
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    ${size(20)}
  }
`;

const ElementIconWrap = styled.span`
  position: absolute;
  top: 20%;
  right: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    right: 10px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    right: 20px;
  }
`;

const DreamTeamWrap = styled.div`
  position: absolute;
  top: 40%;
  right: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    right: 10px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    right: 20px;
  }
`;

interface IOwnProps {
  actionMe: () => void;
  chipName: string | null;
  pick: IPickLight;
  renderDreamTeam: (pick: IPickLight) => React.ReactNode;
  renderElementMenu: () => void;
  renderPickValue: (pick: IPickLight) => React.ReactNode;
  eventId?: number;
}

const TeamPitchElement = ({
  actionMe,
  chipName,
  pick,
  renderElementMenu,
  renderPickValue,
  renderDreamTeam,
  eventId,
}: IOwnProps) => {
  const elementsById = useSelector(getElementsById);
  const savedPicks = useSelector(getMyPicksSaved);
  const proposedPicks = useSelector(getMyPicksProposed);
  const proposedPick = proposedPicks.find(
    (proposed) => proposed.element === pick.element,
  );
  const savedPick = savedPicks.find((saved) => saved.element === pick.element);
  const rules = useSelector(getRules);

  if (!rules) return null;

  const startersCount = rules.squad_squadplay;
  const hasPersistentHighlight = Boolean(
    eventId == null &&
    savedPick &&
    proposedPick &&
    savedPick.position <= startersCount !==
      proposedPick.position <= startersCount,
  );

  const element = elementsById[pick.element];
  const highlightVariant: SubStatus =
    pick.subStatus || (hasPersistentHighlight ? "target" : "");

  // When eventId is not provided (e.g. My Team page), always use "default" - no fixture status context
  const fixturesStatus = useSelector((state: RootState) =>
    eventId != null
      ? getFixturesStatusForTeamInEvent(state, eventId, element.team)
      : null,
  );
  const variant = getElementValueBarVariant(fixturesStatus);

  return (
    <PitchElementWrap variant={highlightVariant}>
      <StyledPitchElement>
        <UnstyledButton type="button" onClick={renderElementMenu}>
          <ElementShirt
            elementId={element.id}
            hasShadow={true}
            sizes="(min-width: 1024px) 55px, (min-width: 610px) 44px, 33px"
          />
          <ElementValueBar variant={variant}>
            <PitchElementData element={element} value={renderPickValue(pick)} />
          </ElementValueBar>
        </UnstyledButton>
        <Controls>
          {actionMe && typeof pick.subStatus !== "undefined" && (
            <PrimaryControl>
              <UnstyledButton type="button" onClick={actionMe}>
                <StyledSub />
              </UnstyledButton>
            </PrimaryControl>
          )}
          <InfoControl>
            <ElementDialogButton elementId={pick.element} />
          </InfoControl>
        </Controls>

        <ElementIconWrap>
          <TeamElementIcon pick={pick} chipName={chipName} />
        </ElementIconWrap>

        <DreamTeamWrap>{renderDreamTeam(pick)}</DreamTeamWrap>
      </StyledPitchElement>
    </PitchElementWrap>
  );
};

export default TeamPitchElement;
